import { apiBlog } from "@/api/blog.api.ts";
import { STRETCH_MODAL_CONTENT_VIEWPORT_OFFSET_PX } from "@/constants/ui/stretch-modal-content.constants.ts";
import i18n from "@/i18n.ts";
import { isBlogAttachmentPreviewable } from "@/types/blog.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import {
  ButtonIcon,
  DocumentPreviewModal,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  useToasts,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface BlogAttachmentPreviewModalProps extends ModalProps {
  postId: number;
  file: ApiFile;
}

export function BlogAttachmentPreviewModal({
  postId,
  file,
  ...modalProps
}: BlogAttachmentPreviewModalProps) {
  const { showError } = useToasts();
  const { t } = useTranslation();
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mimeType = file.mime_type.toLowerCase();
  const isImage = mimeType.startsWith("image/");
  const isVideo = mimeType.startsWith("video/");
  const isAudio = mimeType.startsWith("audio/");
  const isPreviewable = isBlogAttachmentPreviewable(file);

  useEffect(() => {
    let isActive = true;

    void (async () => {
      setIsLoading(true);
      const { data } = await apiBlog.getAttachmentLink(postId, file.uuid);
      if (!isActive) return;

      setFileUrl(data?.download_url ?? null);
      setIsLoading(false);
      if (!data?.download_url) {
        showError({ title: i18n.t("api_errors:blog_attachment_not_found") });
      }
    })();

    return () => {
      isActive = false;
    };
  }, [file.uuid, postId, showError]);

  const openFile = useCallback(() => {
    if (fileUrl) window.open(fileUrl, "_blank", "noopener,noreferrer")?.focus();
  }, [fileUrl]);

  const toggleFullscreen = useCallback(async () => {
    const container = previewContainerRef.current;
    if (!container) return;

    if (document.fullscreenElement === container) {
      await document.exitFullscreen();
    } else {
      await container.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === previewContainerRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const mediaActions = (
    <Stack direction="horizontal" gap="sm" items="center">
      <ButtonIcon
        variant="secondary"
        size="md"
        title={t("common:download")}
        onClick={openFile}
        disabled={!fileUrl}
        icon={<FontAwesomeIcon icon="fa-light fa-download" className="text-[20px]" />}
      />
      <ButtonIcon
        variant="secondary"
        size="md"
        title={t("common:open_new_tab")}
        onClick={openFile}
        disabled={!fileUrl}
        icon={
          <FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" className="text-[20px]" />
        }
      />
      {!isAudio && (
        <ButtonIcon
          variant="secondary"
          size="md"
          title={t(isFullscreen ? "common:fullscreen_exit" : "common:fullscreen")}
          onClick={() => void toggleFullscreen()}
          disabled={!fileUrl}
          icon={
            <FontAwesomeIcon
              icon={isFullscreen ? "fa-light fa-compress" : "fa-light fa-expand"}
              className="text-[20px]"
            />
          }
        />
      )}
    </Stack>
  );

  if (isAudio) {
    return (
      <Modal
        {...modalProps}
        size="md"
        withCloser
        closeByEscEnabled
        title={file.name}
        headerActionsSlot={mediaActions}
      >
        <div className="px-6 pb-6">
          {isLoading ? (
            <Skeleton className="h-14 w-full" />
          ) : (
            fileUrl && <audio src={fileUrl} className="w-full" controls preload="metadata" />
          )}
        </div>
      </Modal>
    );
  }

  if (isImage || isVideo) {
    return (
      <Modal
        {...modalProps}
        layout="stretch"
        size="xl"
        withCloser
        closeByEscEnabled
        disableAutofocus
        title={file.name}
        headerActionsSlot={mediaActions}
      >
        <div
          ref={previewContainerRef}
          className="relative min-h-0 overflow-hidden"
          style={{
            height: isFullscreen
              ? "100dvh"
              : `calc(100dvh - ${STRETCH_MODAL_CONTENT_VIEWPORT_OFFSET_PX}px)`,
            maxHeight: isFullscreen
              ? "100dvh"
              : `calc(100dvh - ${STRETCH_MODAL_CONTENT_VIEWPORT_OFFSET_PX}px)`,
          }}
        >
          {isFullscreen && <div className="absolute right-3 top-3 z-30">{mediaActions}</div>}
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <Stack
              items={"center"}
              justify={"center"}
              className="h-full overflow-auto rounded-xs border border-dark/10 bg-dark/2 p-2"
            >
              {fileUrl && isImage && (
                <img
                  src={fileUrl}
                  alt={file.name}
                  className="mx-auto h-auto max-h-full w-auto max-w-full object-contain"
                />
              )}
              {fileUrl && isVideo && (
                <video src={fileUrl} className="max-h-full max-w-full" controls preload="metadata">
                  <track kind="captions" />
                </video>
              )}
            </Stack>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <DocumentPreviewModal
      {...modalProps}
      title={file.name}
      previewUrl={fileUrl}
      contentType={file.mime_type}
      fileName={file.name}
      status={isLoading ? "loading" : isPreviewable && fileUrl ? "ready" : "unsupported"}
      isLoading={isLoading}
      contentOffsetPx={STRETCH_MODAL_CONTENT_VIEWPORT_OFFSET_PX}
      onDownload={fileUrl ? openFile : undefined}
      onOpenInNewTab={fileUrl ? openFile : undefined}
    />
  );
}
